import { ColDef, ValueFormatterFunc } from "ag-grid-community";
import { useMemo } from "react";

export enum DiffType {
    IncreaseInB = "increaseInB",
    DecreaseInB = "decreaseInB",
    Unchanged = "unchanged",
    OnlyA = "onlyA",
    OnlyB = "onlyB"
}

type Delta = DiffType.IncreaseInB | DiffType.DecreaseInB;

export type DeltaFormat = "percent" | "absolute" | "both";

type JoinColumn<TRow> = {
    kind: "join";
    colId: string;
    headerName: string;
    getValue: (row: TRow) => string | number;
    flex?: number;
    width?: number;
    tooltip?: boolean;
};

type DisplayColumn<TRow> = {
    kind: "display";
    colId: string;
    headerName: string;
    getValue: (rep: TRow) => unknown;
    flex?: number;
    width?: number;
    tooltip?: boolean;
    cellRenderer?: ColDef["cellRenderer"];
};

export type CompareColumn<TRow> = {
    kind: "compare";
    colId: string;
    headerName: string;
    getValue: (row: TRow) => number | null;
    valueFormatter?: ValueFormatterFunc;
    sort?: "asc" | "desc";
    flex?: number;
    deltaFormat?: DeltaFormat;
    hideDelta?: boolean;
    // Whether this column appears in the filter-bar column dropdown.
    // Defaults to true; set false to exclude.
    filterable?: boolean;
};

export type TextCompareColumn<TRow> = {
    kind: "compare-text";
    colId: string;
    headerName: string;
    getValue: (row: TRow) => string;
    flex?: number;
    width?: number;
};

export type CompareTableColumn<TRow> =
    | JoinColumn<TRow>
    | DisplayColumn<TRow>
    | CompareColumn<TRow>
    | TextCompareColumn<TRow>;

export type MergedRow<TRow> = {
    key: string;
    a: TRow | null;
    b: TRow | null;
    onlyIn: "a" | "b" | null;
    // Per-compare-column direction of change. Missing colId means the column
    // was unchanged (or one side was null). Empty when onlyIn !== null.
    deltasByCol: Readonly<Record<string, Delta>>;
};

export type RowChecks = Record<DiffType, boolean>;

// Default: nothing checked == no filter applied (show everything). Toggling
// a bucket on filters the view to that bucket (union across active checks).
export const DEFAULT_ROW_CHECKS: RowChecks = {
    [DiffType.IncreaseInB]: false,
    [DiffType.DecreaseInB]: false,
    [DiffType.Unchanged]: false,
    [DiffType.OnlyA]: false,
    [DiffType.OnlyB]: false
};

export type RowCounts = Record<DiffType, number>;

type RowCountsByCol = Readonly<Record<string, RowCounts>>;

const COMPARE_DELTA_EPSILON = 0.001;

// Conventional relative change: (b - a) / |a|. Undefined when a is zero; Only relevant is diff is ≥ 0.1%
// callers that need a sortable/displayable value should special-case a === 0.
export const isRelativeDeltaSignificant = (a: number, b: number): boolean => {
    if (a === 0 && b === 0) {
        return false;
    }
    if (a === 0) {
        return true;
    }
    return Math.abs((b - a) / a) >= COMPARE_DELTA_EPSILON;
};

const computeDeltasByCol = <TRow>(
    a: TRow,
    b: TRow,
    compareCols: ReadonlyArray<CompareColumn<TRow>>
): Record<string, Delta> => {
    const result: Record<string, Delta> = {};
    for (const c of compareCols) {
        const av = c.getValue(a);
        const bv = c.getValue(b);
        if (av == null || bv == null || !isRelativeDeltaSignificant(av, bv)) {
            continue;
        }
        result[c.colId] = bv > av ? DiffType.IncreaseInB : DiffType.DecreaseInB;
    }
    return result;
};

const buildJoinKey = <TRow>(
    row: TRow,
    joinCols: ReadonlyArray<JoinColumn<TRow>>
): string => JSON.stringify(joinCols.map((c) => c.getValue(row)));

const buildMergedRows = <TRow>(
    rowsA: ReadonlyArray<TRow>,
    rowsB: ReadonlyArray<TRow>,
    joinCols: ReadonlyArray<JoinColumn<TRow>>,
    compareCols: ReadonlyArray<CompareColumn<TRow>>
): MergedRow<TRow>[] => {
    const mapA = new Map<string, TRow>();
    rowsA.forEach((r) => mapA.set(buildJoinKey(r, joinCols), r));

    const mapB = new Map<string, TRow>();
    rowsB.forEach((r) => mapB.set(buildJoinKey(r, joinCols), r));

    const allKeys = new Set<string>();
    mapA.forEach((_, k) => allKeys.add(k));
    mapB.forEach((_, k) => allKeys.add(k));

    return Array.from(allKeys).map((key) => {
        const a = mapA.get(key) ?? null;
        const b = mapB.get(key) ?? null;
        const onlyIn: "a" | "b" | null = !a ? "b" : !b ? "a" : null;
        const deltasByCol = a && b ? computeDeltasByCol(a, b, compareCols) : {};
        return { key, a, b, onlyIn, deltasByCol };
    });
};

const splitColumns = <TRow>(
    columns: ReadonlyArray<CompareTableColumn<TRow>>
): {
    joinCols: ReadonlyArray<JoinColumn<TRow>>;
    compareCols: ReadonlyArray<CompareColumn<TRow>>;
} => ({
    joinCols: columns.filter((c): c is JoinColumn<TRow> => c.kind === "join"),
    compareCols: columns.filter(
        (c): c is CompareColumn<TRow> => c.kind === "compare"
    )
});

const emptyCounts = (): RowCounts => ({
    [DiffType.IncreaseInB]: 0,
    [DiffType.DecreaseInB]: 0,
    [DiffType.Unchanged]: 0,
    [DiffType.OnlyA]: 0,
    [DiffType.OnlyB]: 0
});

const countByColumn = <TRow>(
    merged: ReadonlyArray<MergedRow<TRow>>,
    compareCols: ReadonlyArray<CompareColumn<TRow>>
): RowCountsByCol => {
    // Provides top level counts of:
    //   - [Irrespective of filter column] How many rows are only in A
    //   - [Irrespective of filter column] How many rows are only in B
    //   - [Based on filter column] How many rows increased in B
    //   - [Based on filter column] How many rows decreased in B
    //   - [Based on filter column] How many rows were unchanged in B

    const result: Record<string, RowCounts> = {};
    for (const col of compareCols) {
        result[col.colId] = emptyCounts();
    }
    for (const m of merged) {
        if (m.onlyIn === "a") {
            for (const col of compareCols) {
                result[col.colId][DiffType.OnlyA]++;
            }
            continue;
        }
        if (m.onlyIn === "b") {
            for (const col of compareCols) {
                result[col.colId][DiffType.OnlyB]++;
            }
            continue;
        }
        for (const col of compareCols) {
            const d = m.deltasByCol[col.colId];
            if (d === DiffType.IncreaseInB) {
                result[col.colId][DiffType.IncreaseInB]++;
            } else if (d === DiffType.DecreaseInB) {
                result[col.colId][DiffType.DecreaseInB]++;
            } else {
                result[col.colId][DiffType.Unchanged]++;
            }
        }
    }
    return result;
};

const matchesRowChecks = <TRow>(
    m: MergedRow<TRow>,
    rowChecks: RowChecks,
    columnToFilterBy: string
): boolean => {
    const anyActive =
        rowChecks[DiffType.OnlyA] ||
        rowChecks[DiffType.OnlyB] ||
        rowChecks[DiffType.DecreaseInB] ||
        rowChecks[DiffType.IncreaseInB] ||
        rowChecks[DiffType.Unchanged];
    if (!anyActive) {
        return true;
    }
    if (m.onlyIn === "a") {
        return rowChecks[DiffType.OnlyA];
    }
    if (m.onlyIn === "b") {
        return rowChecks[DiffType.OnlyB];
    }
    const delta = m.deltasByCol[columnToFilterBy];
    if (delta === DiffType.IncreaseInB) {
        return rowChecks[DiffType.IncreaseInB];
    }
    if (delta === DiffType.DecreaseInB) {
        return rowChecks[DiffType.DecreaseInB];
    }
    return rowChecks[DiffType.Unchanged];
};

// Substring match against join, display, and compare-text columns — the
// user-visible identifying fields.
const matchesText = <TRow>(
    m: MergedRow<TRow>,
    needle: string,
    columns: ReadonlyArray<CompareTableColumn<TRow>>
): boolean => {
    const row = m.a ?? m.b;
    if (!row) {
        return false;
    }
    for (const col of columns) {
        if (col.kind === "compare") {
            continue;
        }
        const formatted = String(col.getValue(row) ?? "");
        if (formatted.toLowerCase().includes(needle)) {
            return true;
        }
    }
    return false;
};

export const useMergedView = <TRow>(
    rowsA: ReadonlyArray<TRow>,
    rowsB: ReadonlyArray<TRow>,
    columns: ReadonlyArray<CompareTableColumn<TRow>>,
    rowChecks: RowChecks,
    filterText: string,
    columnToFilterBy: string
): {
    merged: ReadonlyArray<MergedRow<TRow>>;
    displayed: ReadonlyArray<MergedRow<TRow>>;
    rowCountsByCol: RowCountsByCol;
} =>
    useMemo(() => {
        const { joinCols, compareCols } = splitColumns(columns);
        const merged = buildMergedRows(rowsA, rowsB, joinCols, compareCols);
        const needle = filterText.trim().toLowerCase();
        const textFiltered =
            needle.length > 0
                ? merged.filter((m) => matchesText(m, needle, columns))
                : merged;
        const rowCountsByCol = countByColumn(textFiltered, compareCols);
        const displayed = textFiltered.filter((m) =>
            matchesRowChecks(m, rowChecks, columnToFilterBy)
        );
        return { merged, displayed, rowCountsByCol };
    }, [rowsA, rowsB, columns, rowChecks, filterText, columnToFilterBy]);

export const buildFullMerge = <TRow>(
    rowsA: ReadonlyArray<TRow>,
    rowsB: ReadonlyArray<TRow>,
    columns: ReadonlyArray<CompareTableColumn<TRow>>
): ReadonlyArray<MergedRow<TRow>> => {
    const { joinCols, compareCols } = splitColumns(columns);
    return buildMergedRows(rowsA, rowsB, joinCols, compareCols);
};

export const uniqueJoinKeyCount = <TRow>(
    rowsA: ReadonlyArray<TRow>,
    rowsB: ReadonlyArray<TRow>,
    columns: ReadonlyArray<CompareTableColumn<TRow>>
): number => {
    const { joinCols } = splitColumns(columns);
    const keys = new Set<string>();
    rowsA.forEach((r) => keys.add(buildJoinKey(r, joinCols)));
    rowsB.forEach((r) => keys.add(buildJoinKey(r, joinCols)));
    return keys.size;
};
